// /forge/outreach-signals — coordinator triage view built on the Unit 6 v2
// per-applicant outreach extract (Sonnet reads Candidate_Notes blob).
//
// Surfaces per-applicant metrics the existing stall detector can't compute:
// outreach attempts total, inbound response count, last inbound, days
// since last inbound/outbound, outreach mix by channel, Sonnet-extracted
// themes + outcome note.
//
// Sort defaults to outreach deficit — high outreach with no inbound in
// 14+ days goes to the top so coordinators see "ghosted" applicants first.

import { useEffect, useMemo, useState } from "react";

interface OutreachRow {
  applicant_airtable_id: string;
  outreach_attempts_total: number;
  outbound_sms: number;
  outbound_email: number;
  outbound_phone: number;
  inbound_response_count: number;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  days_since_last_inbound: number | null;
  days_since_last_outbound: number | null;
  outcome_note: string | null;
  themes: string[];
  rationale: string | null;
}

interface Response {
  applicants: OutreachRow[];
  count: number;
  note: string | null;
}

type SortKey = "deficit" | "attempts" | "silence";

const GHOST_THRESHOLD_DAYS = 14;

export default function OutreachSignals() {
  const [data, setData] = useState<Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [sort, setSort] = useState<SortKey>("deficit");

  useEffect(() => {
    let cancelled = false;
    const url = `/api/forge/applicant-outreach?limit=200${activeOnly ? "&active_only=1" : ""}`;
    fetch(url, { credentials: "same-origin" })
      .then((r) =>
        r.ok
          ? (r.json() as Promise<Response>)
          : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [activeOnly]);

  const sorted = useMemo(() => {
    const rows = data?.applicants ?? [];
    const copy = [...rows];
    switch (sort) {
      case "deficit":
        // "Ghost score" — high outbound attempts with no/stale inbound wins
        copy.sort((a, b) => {
          const aScore =
            a.outreach_attempts_total *
              ((a.days_since_last_inbound ?? 999) / 30) -
            a.inbound_response_count * 2;
          const bScore =
            b.outreach_attempts_total *
              ((b.days_since_last_inbound ?? 999) / 30) -
            b.inbound_response_count * 2;
          return bScore - aScore;
        });
        break;
      case "attempts":
        copy.sort(
          (a, b) => b.outreach_attempts_total - a.outreach_attempts_total,
        );
        break;
      case "silence":
        copy.sort((a, b) => {
          const aDays = a.days_since_last_inbound ?? 9999;
          const bDays = b.days_since_last_inbound ?? 9999;
          return bDays - aDays;
        });
        break;
    }
    return copy;
  }, [data, sort]);

  if (error) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-red-600">
        Failed to load: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted">
        Loading outreach signals…
      </div>
    );
  }

  const ghostedCount = sorted.filter(
    (r) =>
      (r.days_since_last_inbound ?? 999) >= GHOST_THRESHOLD_DAYS &&
      r.outreach_attempts_total >= 2,
  ).length;

  return (
    <div className="space-y-6 px-4 md:px-8 py-6 max-w-7xl mx-auto">
      <header>
        <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
          FORGE · Outreach
        </p>
        <h1 className="text-2xl font-bold text-theme-primary tracking-tight">
          Applicant outreach triage
        </h1>
        <p className="text-sm text-theme-secondary mt-1">
          Per-applicant outreach signals from the Sonnet Candidate_Notes
          extractor (Unit 6 v2). Default sort surfaces likely-ghosted applicants
          first — high outbound attempts, stale inbound, few inbound responses.
          These are the ones that need a phone call, not another SMS.
        </p>
      </header>

      {data.note && (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3">
          {data.note}
        </div>
      )}

      <section className="glass rounded-2xl p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="text-xs text-theme-muted">
            <strong className="text-theme-primary">
              {data.count.toLocaleString()}
            </strong>{" "}
            applicants with labeled comments
            {ghostedCount > 0 && (
              <>
                {" · "}
                <strong className="text-amber-700">{ghostedCount}</strong>{" "}
                likely-ghosted (≥2 outbound, {GHOST_THRESHOLD_DAYS}+ days
                silence)
              </>
            )}
          </div>
          <div className="ml-auto flex gap-2 text-xs">
            <label className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Active applicants only
            </label>
            <div className="border-l border-theme-subtle pl-2 ml-1 flex gap-1">
              {[
                { k: "deficit", l: "Ghost score" },
                { k: "attempts", l: "Attempts" },
                { k: "silence", l: "Silence" },
              ].map(({ k, l }) => (
                <button
                  key={k}
                  onClick={() => setSort(k as SortKey)}
                  className={`px-2 py-1 rounded ${
                    sort === k
                      ? "bg-emerald-600 text-white"
                      : "border border-theme-subtle text-theme-secondary hover:bg-theme-overlay"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {sorted.length === 0 ? (
          <p className="text-sm text-theme-muted">
            No outreach data yet. Run{" "}
            <code className="font-mono">
              npx tsx sync/ml-applicant-outreach-extract.ts
            </code>{" "}
            to populate.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
                  <th className="py-2 pr-3">Applicant</th>
                  <th className="py-2 px-3 text-right">Outreach</th>
                  <th className="py-2 px-3 text-right">SMS</th>
                  <th className="py-2 px-3 text-right">Email</th>
                  <th className="py-2 px-3 text-right">Phone</th>
                  <th className="py-2 px-3 text-right">Inbound</th>
                  <th className="py-2 px-3 text-right">Silence</th>
                  <th className="py-2 px-3 text-right">Last inbound</th>
                  <th className="py-2 pl-3">Themes / outcome</th>
                </tr>
              </thead>
              <tbody>
                {sorted.slice(0, 100).map((r) => {
                  const ghosted =
                    (r.days_since_last_inbound ?? 999) >=
                      GHOST_THRESHOLD_DAYS && r.outreach_attempts_total >= 2;
                  return (
                    <tr
                      key={r.applicant_airtable_id}
                      className={`hover:bg-theme-overlay/50 transition-colors ${ghosted ? "bg-amber-50 dark:bg-amber-900/10" : ""}`}
                    >
                      <td className="py-2 pr-3 font-mono text-theme-primary text-xs">
                        {r.applicant_airtable_id}
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-primary font-semibold">
                        {r.outreach_attempts_total}
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                        {r.outbound_sms}
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                        {r.outbound_email}
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                        {r.outbound_phone}
                      </td>
                      <td
                        className={`py-2 px-3 text-right font-mono tabular-nums ${r.inbound_response_count > 0 ? "text-emerald-600" : "text-theme-muted"}`}
                      >
                        {r.inbound_response_count}
                      </td>
                      <td
                        className={`py-2 px-3 text-right font-mono tabular-nums ${ghosted ? "text-amber-700 font-semibold" : "text-theme-muted"}`}
                      >
                        {r.days_since_last_inbound === null
                          ? "—"
                          : `${r.days_since_last_inbound}d`}
                      </td>
                      <td className="py-2 px-3 text-right text-xs text-theme-muted tabular-nums">
                        {r.last_inbound_at
                          ? r.last_inbound_at.slice(0, 10)
                          : "—"}
                      </td>
                      <td className="py-2 pl-3 text-xs text-theme-muted max-w-xs">
                        {r.themes.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {r.themes.map((t) => (
                              <span
                                key={t}
                                className="inline-block rounded bg-theme-overlay px-1.5 py-0.5 text-[10px] text-theme-secondary"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {r.outcome_note && (
                          <div
                            className="text-[11px] text-theme-secondary italic line-clamp-2"
                            title={r.outcome_note}
                          >
                            {r.outcome_note}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {sorted.length > 100 && (
              <p className="text-xs text-theme-muted mt-3 italic">
                Showing top 100 of {sorted.length.toLocaleString()}.
              </p>
            )}
          </div>
        )}
      </section>

      <footer className="text-xs text-theme-muted">
        <p>
          <strong className="text-theme-secondary">Ghost score</strong> =
          outreach_attempts × (days_since_last_inbound / 30) −
          inbound_response_count × 2. Sourced from{" "}
          <code className="font-mono">dash_applicant_outreach_extracts</code>{" "}
          (Sonnet reads <code className="font-mono">Candidate_Notes</code> per
          applicant).
        </p>
      </footer>
    </div>
  );
}
